/**
 * Canvas drawing and frame preloading utilities for the Avian Canvas Sequence.
 * Zero `any` types.
 */

// * Paints a frame with high-DPI aspect-ratio cover math and optional vertical framing offset
export const drawAvianFrame = (
  canvas: HTMLCanvasElement | null,
  images: HTMLImageElement[],
  frameIndex: number,
  totalFrames: number,
  applyAboutOffset: boolean = false
): void => {
  try {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const clampedIndex = Math.max(0, Math.min(totalFrames - 1, frameIndex));
    let img = images[clampedIndex];

    // ? Fallback search for nearest loaded frame to prevent visual flicker during scrub
    if (!img || !img.complete) {
      for (let offset = 1; offset < 25; offset++) {
        const prev = images[clampedIndex - offset];
        if (prev && prev.complete) {
          img = prev;
          break;
        }
        const next = images[clampedIndex + offset];
        if (next && next.complete) {
          img = next;
          break;
        }
      }
    }

    if (!img || !img.complete) return;

    const cw = canvas.width;
    const ch = canvas.height;
    const hRatio = cw / img.width;
    const vRatio = ch / img.height;
    const ratio = Math.max(hRatio, vRatio);

    const shiftX = (cw - img.width * ratio) / 2;

    // * Section Checkpoint 50 (About section) downward framing bias
    const offsetY = applyAboutOffset
      ? ch * 0.065 * Math.max(0, 1 - Math.abs(clampedIndex - 50) / 25)
      : 0;

    const shiftY = (ch - img.height * ratio) / 2 + offsetY;

    ctx.clearRect(0, 0, cw, ch);
    ctx.drawImage(
      img,
      0,
      0,
      img.width,
      img.height,
      shiftX,
      shiftY,
      img.width * ratio,
      img.height * ratio
    );
  } catch (err) {
    console.error('[avianPreloader] drawFrame error:', err);
  }
};

// * Starts staged progressive preloading for canvas sequence frames
export const startAvianPreload = (
  pathPrefix: string,
  totalFrames: number,
  onFrame0Ready: () => void
): { images: HTMLImageElement[]; cleanup: () => void } => {
  const images: HTMLImageElement[] = new Array(totalFrames);
  let isCancelled = false;
  let idleTimer: ReturnType<typeof setTimeout> | null = null;

  // * 1. Immediate priority: Frame 0
  const frame0 = new Image();
  frame0.src = `${pathPrefix}/frame_000.webp`;
  images[0] = frame0;

  const handleFrame0 = () => {
    if (!isCancelled) {
      onFrame0Ready();
    }
  };

  if (frame0.complete) {
    handleFrame0();
  } else {
    frame0.onload = handleFrame0;
  }

  // * 2. Checkpoint frames preloaded next for smooth scrub points
  const checkpoints = [50, 75, 95, 100, 119, 150, 200];
  checkpoints.forEach((idx) => {
    if (idx < totalFrames && !images[idx]) {
      const img = new Image();
      img.src = `${pathPrefix}/frame_${String(idx).padStart(3, '0')}.webp`;
      images[idx] = img;
    }
  });

  // * 3. Progressive streaming of remaining frames
  let nextFrame = 1;
  const loadNextBatch = () => {
    if (isCancelled) return;
    const batchSize = 6;
    let loaded = 0;

    while (nextFrame < totalFrames && loaded < batchSize) {
      if (!images[nextFrame]) {
        const img = new Image();
        img.src = `${pathPrefix}/frame_${String(nextFrame).padStart(3, '0')}.webp`;
        images[nextFrame] = img;
        loaded++;
      }
      nextFrame++;
    }

    if (nextFrame < totalFrames) {
      idleTimer = setTimeout(loadNextBatch, 50);
    }
  };

  idleTimer = setTimeout(loadNextBatch, 150);

  const cleanup = () => {
    isCancelled = true;
    if (idleTimer) clearTimeout(idleTimer);
  };

  return { images, cleanup };
};
